import React, { useState } from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  MenuList,
  Select,
  MenuItem,
  Typography,
} from "@material-ui/core";

function ImageGroupSelectModal(props: ImageGroupSelectModalProps) {
  const classes = useStyles();
  const [selectedImage, setSelectedImage] = useState<AssetItem>(props.group[0]); // Default to the "-large" image
  const [imgDimensions, setImgDimensions] = useState({ width: 0, height: 0 });
  const handleSelectChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selected = props.group.find((img) =>
      img.asset
        ? img.asset.url === event.target.value
        : img.url === event.target.value
    );
    if (selected) {
      setSelectedImage(selected);
    }
  };

  // Capture image dimensions
  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    setImgDimensions({ width: naturalWidth, height: naturalHeight });
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="xl"
      PaperProps={{
        style: {
          width: "70vw",
          height: "90vh",
        },
      }}
    >
      <DialogTitle className={classes.title}>Image Preview</DialogTitle>

      {/* Dropdown to choose between images */}
      <Select
        fullWidth
        value={
          selectedImage.asset ? selectedImage.asset.url : selectedImage.url
        }
        onChange={handleSelectChange}
        className={classes.dropdown}
      >
        {props.group.map((image) => (
          <MenuItem
            key={image.asset ? image.asset.url : image.url}
            value={image.asset ? image.asset.url : image.url}
          >
            {image.name}
          </MenuItem>
        ))}
      </Select>

      <DialogContent className={classes.dialogContent}>
        {/* Render the selected image */}
        <img
          src={
            selectedImage.asset ? selectedImage.asset.url : selectedImage.url
          }
          alt={selectedImage.name}
          className={classes.imagePreview}
          onLoad={handleImageLoad}
        />
      </DialogContent>

      {/* renders the selected image dimensions */}
      <div className={classes.imageDetails}>
        <Typography variant="body2">
          Dimensions: {imgDimensions.width} x {imgDimensions.height} px
        </Typography>
      </div>

      {/* handles menu actions */}
      <MenuList className={classes.actions}>
        <MenuItem
          onClick={() => {
            const url = selectedImage.asset
              ? selectedImage.asset.url
              : selectedImage.url;
            if (url) {
              props.handleCopyToClipboard(url);
            }
          }}
          color="primary"
        >
          <Typography>Copy File URL</Typography>
        </MenuItem>
        <MenuItem onClick={props.handleClickOpenDelete}>
          <Typography>Delete File</Typography>
        </MenuItem>
        <MenuItem onClick={props.onClose} color="default">
          <Typography>Close</Typography>
        </MenuItem>
      </MenuList>
    </Dialog>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogContent: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
    },
    dropdown: {
      maxWidth: "600px",
      margin: "0 auto",
    },
    imagePreview: {
      maxWidth: "100%",
      maxHeight: "100%",
      objectFit: "contain",
    },
    title: {
      margin: "0 auto",
    },
    actions: {
      margin: "0 auto",
      display: "flex",
    },
    imageDetails: {
      marginTop: theme.spacing(2),
      textAlign: "center",
    },
  })
);

interface ImageGroupSelectModalProps {
  open: boolean;
  onClose: any;
  onDelete: any;
  group: AssetItem[];
  handleClickOpenDelete: () => void;
  handleCopyToClipboard: (url: string) => void;
}

interface AssetItem {
  created_at: string;
  id: string;
  metadata: Metadata;
  name: string;
  physical_file_path: string;
  updated_at: string;
  url?: string; // Make this optional in case it's nested under the `asset` property.
  asset?: {
    url: string;
    physical_file_path: string;
    name: string;
  }; // Optional in case the asset details are nested inside another object.
  type?: string; // Optional in case the `type` field is used in some parts.
}

interface Metadata {
  group_id?: string;
}

export default ImageGroupSelectModal;
